import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

// Attach Bearer token from localStorage on every request
client.interceptors.request.use((config) => {
  const token = localStorage.getItem("cv_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ---- Content ----
export const getConfig = () => client.get("/config").then((r) => r.data);
export const getSiteCopy = () => client.get("/content/site-copy").then((r) => r.data);
export const getServices = () => client.get("/content/services").then((r) => r.data);
export const getService = (slug) => client.get(`/content/services/${slug}`).then((r) => r.data);
export const getCaseStudies = () => client.get("/content/case-studies").then((r) => r.data);
export const getCaseStudy = (slug) =>
  client.get(`/content/case-studies/${slug}`).then((r) => r.data);
export const getLogoRoster = () => client.get("/content/logo-roster").then((r) => r.data);
export const getResources = () => client.get("/content/resources").then((r) => r.data);
export const getEvents = () => client.get("/content/events").then((r) => r.data);
export const getEvent = (slug) => client.get(`/content/events/${slug}`).then((r) => r.data);
export const rsvpEvent = (slug, p) =>
  client.post(`/events/${slug}/rsvp`, p).then((r) => r.data);
export const getPacketArchivePublic = () =>
  client.get("/content/packet-archive").then((r) => r.data);
export const getFrameworks = () => client.get("/content/frameworks").then((r) => r.data);
export const getFramework = (slug) =>
  client.get(`/content/frameworks/${slug}`).then((r) => r.data);
export const getFieldNotes = () => client.get("/content/field-notes").then((r) => r.data);
export const getFieldNote = (slug) =>
  client.get(`/content/field-notes/${slug}`).then((r) => r.data);
export const getScorecardConfig = () => client.get("/content/scorecard").then((r) => r.data);

// ---- Forms ----
export const submitBrief = (p) => client.post("/leads/brief", p).then((r) => r.data);
export const submitPacket = (p) => client.post("/leads/packet", p).then((r) => r.data);
export const submitPartner = (p) => client.post("/leads/partner", p).then((r) => r.data);
export const submitProduction = (p) => client.post("/leads/production", p).then((r) => r.data);
export const submitScorecard = (p) => client.post("/leads/scorecard", p).then((r) => r.data);
export const getScorecardReport = (p) => client.post("/scorecard/report", p).then((r) => r.data);
export const downloadResource = (p) =>
  client.post("/resources/download", p).then((r) => r.data);
export const referOnePagerUrl = () => `${API}/resources/refer-one-pager`;
export const resourceFileUrl = (fileUrl) => `${BACKEND_URL}${fileUrl}`;

// ---- Commerce ----
export const createCheckoutSession = (p) =>
  client.post("/payments/checkout/session", p).then((r) => r.data);
export const getCheckoutStatus = (id) =>
  client.get(`/payments/checkout/status/${id}`).then((r) => r.data);
export const submitIntake = (p) => client.post("/intake/sprint", p).then((r) => r.data);
export const recordEvent = (p) => client.post("/analytics/event", p).then((r) => r.data);

// ---- Auth ----
export const apiLogin = (p) => client.post("/auth/login", p).then((r) => r.data);
export const apiRegister = (p) => client.post("/auth/register", p).then((r) => r.data);
export const apiSetPassword = (p) => client.post("/auth/set-password", p).then((r) => r.data);
export const apiChangePassword = (p) => client.post("/auth/change-password", p).then((r) => r.data);
export const apiMe = () => client.get("/auth/me").then((r) => r.data);

// ---- Hub ----
export const hubDashboard = () => client.get("/hub/dashboard").then((r) => r.data);
export const hubDeliverables = () => client.get("/hub/deliverables").then((r) => r.data);
export const hubPacketArchive = () => client.get("/hub/packet-archive").then((r) => r.data);
export const hubEvents = () => client.get("/hub/events").then((r) => r.data);
export const hubTable = () => client.get("/hub/table").then((r) => r.data);
export const hubTableIntro = (p) => client.post("/hub/table/intro-request", p).then((r) => r.data);
export const hubCohort = () => client.get("/hub/cohort").then((r) => r.data);
export const hubAccount = () => client.get("/hub/account").then((r) => r.data);
export const hubBillingPortal = () => client.post("/hub/billing-portal", {}).then((r) => r.data);

// ---- Admin ----
export const adminOverview = () => client.get("/admin/overview").then((r) => r.data);
export const adminGetSiteCopy = () => client.get("/admin/site-copy").then((r) => r.data);
export const adminPutSiteCopy = (p) => client.put("/admin/site-copy", p).then((r) => r.data);
export const adminGetCaseStudies = () => client.get("/admin/case-studies").then((r) => r.data);
export const adminUpsertCaseStudy = (p) => client.post("/admin/case-studies", p).then((r) => r.data);
export const adminDeleteCaseStudy = (id) =>
  client.delete(`/admin/case-studies/${id}`).then((r) => r.data);
export const adminUploadCaseOnePager = (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post(`/admin/case-studies/${id}/one-pager`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const adminDeleteCaseOnePager = (id) =>
  client.delete(`/admin/case-studies/${id}/one-pager`).then((r) => r.data);
export const adminGetServices = () => client.get("/admin/services").then((r) => r.data);
export const adminPutService = (p) => client.put("/admin/services", p).then((r) => r.data);
export const adminGetRoster = () => client.get("/admin/logo-roster").then((r) => r.data);
export const adminAddRoster = (p) => client.post("/admin/logo-roster", p).then((r) => r.data);
export const adminDeleteRoster = (group, name) =>
  client.delete(`/admin/logo-roster`, { params: { group, name } }).then((r) => r.data);
export const adminGetEvents = () => client.get("/admin/events").then((r) => r.data);
export const adminUpsertEvent = (p) => client.post("/admin/events", p).then((r) => r.data);
export const adminEventStatus = (slug, status) =>
  client.post(`/admin/events/${slug}/status`, null, { params: { status } }).then((r) => r.data);
export const adminDeleteEvent = (slug) => client.delete(`/admin/events/${slug}`).then((r) => r.data);
export const adminCheckActivation = (slug) =>
  client.post(`/admin/events/${slug}/check-activation`, {}).then((r) => r.data);
export const adminGetEventRegistrations = (slug) =>
  client.get(`/admin/events/${slug}/registrations`).then((r) => r.data);
export const adminPublishPacket = (p) => client.post("/admin/packet/publish", p).then((r) => r.data);

// ---- Admin: Frameworks ----
export const adminGetFrameworks = () => client.get("/admin/frameworks").then((r) => r.data);
export const adminUpsertFramework = (p) => client.post("/admin/frameworks", p).then((r) => r.data);
export const adminDeleteFramework = (id) =>
  client.delete(`/admin/frameworks/${id}`).then((r) => r.data);
export const adminUploadFrameworkFile = (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return client.post(`/admin/frameworks/${id}/file`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const adminDeleteFrameworkFile = (id) =>
  client.delete(`/admin/frameworks/${id}/file`).then((r) => r.data);

// ---- Admin: Field Notes ----
export const adminGetFieldNotes = () => client.get("/admin/field-notes").then((r) => r.data);
export const adminUpsertFieldNote = (p) => client.post("/admin/field-notes", p).then((r) => r.data);
export const adminDeleteFieldNote = (id) =>
  client.delete(`/admin/field-notes/${id}`).then((r) => r.data);

// ---- Admin: Operator Packet issues (full CRUD) ----
export const adminGetPackets = () => client.get("/admin/packets").then((r) => r.data);
export const adminUpsertPacket = (p) => client.post("/admin/packets", p).then((r) => r.data);
export const adminDeletePacket = (id) => client.delete(`/admin/packets/${id}`).then((r) => r.data);

// ---- Admin: Readiness Scorecard config ----
export const adminGetScorecardConfig = () => client.get("/admin/scorecard").then((r) => r.data);
export const adminUpdateScorecardConfig = (p) =>
  client.put("/admin/scorecard", p).then((r) => r.data);

export const adminGetAudit = () => client.get("/admin/audit").then((r) => r.data);

// ---- Admin: Mini-CRM ----
export const adminGetCrmSubmissions = (params = {}) =>
  client.get("/admin/crm/submissions", { params }).then((r) => r.data);
export const adminGetCrmContacts = (params = {}) =>
  client.get("/admin/crm/contacts", { params }).then((r) => r.data);
export const adminUpdateCrmContact = (p) =>
  client.put("/admin/crm/contact", p).then((r) => r.data);

// ---- Admin: Clients (invoice-based manual provisioning) ----
export const adminGetClients = (search = "") =>
  client.get("/admin/clients", { params: { search } }).then((r) => r.data);
export const adminCreateClient = (p) => client.post("/admin/clients", p).then((r) => r.data);
export const adminGetClient = (id) => client.get(`/admin/clients/${id}`).then((r) => r.data);
export const adminUpdateClient = (id, p) => client.put(`/admin/clients/${id}`, p).then((r) => r.data);
export const adminSetClientAccess = (id, grant) =>
  client.post(`/admin/clients/${id}/access`, { grant }).then((r) => r.data);
export const adminResendInvite = (id, origin_url) =>
  client.post(`/admin/clients/${id}/resend-invite`, { origin_url }).then((r) => r.data);
export const adminAddDeliverable = (id, p) =>
  client.post(`/admin/clients/${id}/deliverables`, p).then((r) => r.data);
export const adminDeleteDeliverable = (id, did) =>
  client.delete(`/admin/clients/${id}/deliverables/${did}`).then((r) => r.data);
export const adminAddCohortMaterial = (id, p) =>
  client.post(`/admin/clients/${id}/cohort-materials`, p).then((r) => r.data);
export const adminDeleteCohortMaterial = (id, mid) =>
  client.delete(`/admin/clients/${id}/cohort-materials/${mid}`).then((r) => r.data);

// ---- Auth: invite / welcome ----
export const apiGetInvite = (token) => client.get(`/auth/invite/${token}`).then((r) => r.data);
export const apiAcceptInvite = (p) => client.post("/auth/accept-invite", p).then((r) => r.data);

export default client;
